import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { useDispatch } from 'react-redux';
import type { UnknownAction } from '@reduxjs/toolkit';
import type { ISchedulerTask, LocationStatus } from '@interfaces/IScheduler';
import { useCommittedActivities } from '@components/Pages/ActivitiesPage/hooks/useCommittedActivities';
import { schedulerActions } from '@store/slices/scheduler.slice';
import { ladderOf, type ILadder, type ILadderActivity } from '../lib/ladder';
import { frozenTasks, type CascadeFreeze } from '../lib/freeze';

// ---------------------------------------------------------------------------
// The Scheduler's view of Trusstor's activity status, per task. The schedule
// read is deliberately Trusstor-free (a 502 there must not take the Gantt
// down), so the rail joins the committed-activities feed CLIENT-side: one
// query + one SSE stream for the page (the same hook the Activities page
// uses), folded into a `taskId → activity` map the ladder reads from.
//
// Provided through context as a PER-TASK store, so 300 rows share one feed
// without sharing its churn: a row subscribes to its own task, and a feed
// update re-renders only the rows whose entry actually changed. Both pages
// that host the task drawer — the Scheduler and the Activities page — mount it
// (`usePageActivityLadder`); without the provider (tests) a task reads an
// empty map, and every committed task as plainly "committed".
// ---------------------------------------------------------------------------

/** What the feed knows about one task: Trusstor's word for the group (whose
 *  `complete` is the done-suggestion — `taskStepOf`), its word for each AREA
 *  (`locationId → status`) and the tag work time it has counted there
 *  (`locationId → ms`, only areas with some). */
export type ActivityRow = ILadderActivity & {
  areas: Readonly<Record<string, LocationStatus>>;
  areaWorkTimeMs?: Readonly<Record<string, number>>;
};
export type ActivityByTask = ReadonlyMap<string, ActivityRow>;

/** Where a row reads its task's entry. A plain {@link ActivityByTask} is a
 *  valid STATIC source (what tests and a page without the feed provide); the
 *  Scheduler provides an {@link ActivityLadderStore}, which can also say when
 *  one task's entry changed. */
export interface IActivityLadderSource {
  get(taskId: string): ActivityRow | undefined;
  subscribe?(taskId: string, onChange: () => void): () => void;
}

const EMPTY: ActivityByTask = new Map();
const NO_UNSUBSCRIBE = () => {};

export const ActivityLadderContext = createContext<IActivityLadderSource>(EMPTY);

/** The feed folded by task id — mount once per page, near the rail. */
export function useActivityByTask(siteId: string | undefined): ActivityByTask {
  const { activities } = useCommittedActivities(siteId);
  return useMemo(
    () =>
      new Map(
        activities.map((row) => [
          row.task.id,
          {
            activityStatus: row.activityStatus,
            areas: Object.fromEntries(
              (row.task.locations ?? []).map((location) => [location.locationId, location.status] as const),
            ),
            areaWorkTimeMs: Object.fromEntries(
              (row.task.locations ?? []).flatMap((location) =>
                location.workTimeMs ? [[location.locationId, location.workTimeMs] as const] : [],
              ),
            ),
          },
        ]),
      ),
    [activities],
  );
}

/** Same keys, same values — a flat record of statuses or milliseconds. */
function sameRecord<V>(a: Readonly<Record<string, V>>, b: Readonly<Record<string, V>>): boolean {
  const keys = Object.keys(a);
  return keys.length === Object.keys(b).length && keys.every((key) => a[key] === b[key]);
}

const NO_WORK_TIME: Readonly<Record<string, number>> = {};

/** Two folds of one task agree — its row has nothing new to show. */
function sameRow(a: ActivityRow, b: ActivityRow): boolean {
  return (
    a.activityStatus === b.activityStatus &&
    sameRecord(a.areas, b.areas) &&
    sameRecord(a.areaWorkTimeMs ?? NO_WORK_TIME, b.areaWorkTimeMs ?? NO_WORK_TIME)
  );
}

/**
 * The feed as a per-task store. Every refetch folds a brand-new map; handed to
 * the rows through context as-is, it re-rendered every visible row on every SSE
 * tick. Here each row subscribes to ITS task, and {@link publish} keeps the
 * previous entry wherever the content is unchanged — so an untouched row's
 * snapshot is the same object, and only the rows whose entry changed are woken.
 */
export class ActivityLadderStore implements IActivityLadderSource {
  private byTask: ActivityByTask;
  private readonly listeners = new Map<string, Set<() => void>>();

  constructor(byTask: ActivityByTask) {
    this.byTask = byTask;
  }

  get(taskId: string): ActivityRow | undefined {
    return this.byTask.get(taskId);
  }

  subscribe(taskId: string, onChange: () => void): () => void {
    const listeners = this.listeners.get(taskId) ?? new Set<() => void>();
    this.listeners.set(taskId, listeners);
    listeners.add(onChange);
    return () => {
      listeners.delete(onChange);
      if (listeners.size === 0) this.listeners.delete(taskId);
    };
  }

  /** Take the next fold, and wake only the tasks whose entry changed — or
   *  appeared, or left the feed. */
  publish(next: ActivityByTask): void {
    const previous = this.byTask;
    this.byTask = new Map(
      [...next].map(([taskId, row]) => {
        const before = previous.get(taskId);
        return [taskId, before && sameRow(before, row) ? before : row] as const;
      }),
    );
    for (const [taskId, listeners] of this.listeners) {
      if (this.byTask.get(taskId) !== previous.get(taskId)) listeners.forEach((onChange) => onChange());
    }
  }
}

/** The page's store over {@link useActivityByTask}'s fold — the value for
 *  {@link ActivityLadderContext}. One instance for the page's lifetime. */
export function useActivityLadderStore(byTask: ActivityByTask): ActivityLadderStore {
  const [store] = useState(() => new ActivityLadderStore(byTask));
  // After render (a store must not wake subscribers while React is rendering)
  // and before paint, so a woken row never paints a frame behind the feed.
  useLayoutEffect(() => store.publish(byTask), [store, byTask]);
  return store;
}

/** One task's entry, re-read only when that entry changes. */
function useActivityRow(taskId: string): ActivityRow | undefined {
  const source = useContext(ActivityLadderContext);
  const subscribe = useCallback(
    (onChange: () => void) => source.subscribe?.(taskId, onChange) ?? NO_UNSUBSCRIBE,
    [source, taskId],
  );
  return useSyncExternalStore(subscribe, () => source.get(taskId));
}

/** The ladder rung for one task, from the plan row + the feed's word for it. */
export function useTaskLadder(task: Pick<ISchedulerTask, 'id' | 'status' | 'committed' | 'kind'>): ILadder {
  return ladderOf(task, useActivityRow(task.id));
}

/**
 * Carry an observation of server state into the store: dispatch
 * `mirror(siteId, observed)` whenever its CONTENT changes. The serialized form
 * is the effect's dependency, not the object — a fresh object every pass would
 * dispatch on every render, and a poll returning the same answers must not
 * touch the store.
 */
function useServerStateMirror<T>(
  siteId: string | undefined,
  observed: T,
  mirror: (siteId: string, observed: T) => UnknownAction,
): void {
  const dispatch = useDispatch();
  const key = useMemo(() => JSON.stringify(observed), [observed]);
  useEffect(() => {
    if (!siteId) return;
    dispatch(mirror(siteId, JSON.parse(key) as T));
  }, [dispatch, siteId, key, mirror]);
}

const mirrorFrozenTasks = (siteId: string, frozen: Record<string, CascadeFreeze>) =>
  schedulerActions.setFrozenTasks({ siteId, frozen });

/**
 * Mirror the observed-work anchors into the store, where the dependency cascade
 * can reach them (2026-09-20 amendment, decision 3). The cascade runs inside
 * reducers and the feed is TanStack Query state, so somebody has to carry it
 * across — this hook, mounted once per page beside the feed itself.
 *
 * `frozenBySite` is a MIRROR of server state: this is its only writer, and it
 * is only written when a task's freeze level actually differs.
 */
export function useFrozenTasksMirror(
  siteId: string | undefined,
  tasks: readonly Pick<ISchedulerTask, 'id' | 'status' | 'kind'>[],
  byTask: ActivityByTask,
): void {
  // Key order follows the plan's task order, so equal contents serialize equal.
  const frozen = useMemo(() => Object.fromEntries(frozenTasks(tasks, byTask)), [tasks, byTask]);
  useServerStateMirror(siteId, frozen, mirrorFrozenTasks);
}

interface IObservedAreas {
  byTask: Record<string, Readonly<Record<string, LocationStatus>>>;
  workTimeMs: Record<string, Readonly<Record<string, number>>>;
}

const mirrorAreaStatuses = (siteId: string, observed: IObservedAreas) =>
  schedulerActions.areaStatusesObserved({ siteId, ...observed });

/**
 * Mirror the feed's per-AREA statuses — and the work time the IoT has counted
 * per area — into the store, the same way {@link useFrozenTasksMirror} carries
 * the freeze levels across.
 *
 * Per-area status lives in Trusstor; the schedule read is Trusstor-free by
 * design, so it hands every committed area back as `planned`. Without this the
 * drawer's board would show that guess forever — an area completed in Trusstor,
 * or moved by the IoT to in-progress / delayed / overdue, would never appear.
 *
 * A MIRROR of server state, landed onto the plan copy. Unlike `frozenBySite`
 * it is not its field's only writer: the drawer's optimistic area edit writes
 * area status too, and this confirms or corrects it once the feed reports.
 * Dispatched only when a status actually differs, so a local optimistic write
 * is not undone by re-applying what the feed already agreed with.
 */
export function useAreaStatusMirror(siteId: string | undefined, byTask: ActivityByTask): void {
  // Map iteration order follows the feed's row order, so equal contents
  // serialize equal.
  const observed = useMemo<IObservedAreas>(
    () => ({
      byTask: Object.fromEntries([...byTask].map(([taskId, row]) => [taskId, row.areas])),
      workTimeMs: Object.fromEntries([...byTask].map(([taskId, row]) => [taskId, row.areaWorkTimeMs ?? {}])),
    }),
    [byTask],
  );
  useServerStateMirror(siteId, observed, mirrorAreaStatuses);
}

/**
 * The feed wired for a page that hosts the task drawer: folded by task (the
 * returned `byTask`, for page-wide reads like the Scheduler's status filter),
 * served per task — provide the returned `store` through
 * {@link ActivityLadderContext} — and mirrored into the store: the freeze levels for the cascade, the
 * per-area statuses (and work time) for the drawer's area board. Mount once
 * per page.
 */
export function usePageActivityLadder(
  siteId: string | undefined,
  tasks: readonly Pick<ISchedulerTask, 'id' | 'status' | 'kind'>[],
): { store: ActivityLadderStore; byTask: ActivityByTask } {
  const byTask = useActivityByTask(siteId);
  const store = useActivityLadderStore(byTask);
  useFrozenTasksMirror(siteId, tasks, byTask);
  useAreaStatusMirror(siteId, byTask);
  return { store, byTask };
}
